import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAuth } from "../../../contexts/AuthContext";
import MemberDayEntryDrawer from "./memberEntry/MemberDayEntryDrawer";
import { canEnterFor } from "./memberEntry/memberDayHelpers";
import MemberTitlesPanel from "../../titles/MemberTitles";
import { aggregateGoals, memberRate } from "../../../lib/progressCompare";
import { ACTIVITY_LABEL_KEYS } from "../../goals/goalRules";
import UserAvatar from "../../../components/ui/UserAvatar";

function toneFor(percent) {
  if (percent >= 100) return "bg-terakkiPrimaryDark";
  if (percent >= 67) return "bg-terakkiPrimary";
  if (percent >= 34) return "bg-terakkiWarning";
  if (percent > 0) return "bg-terakkiError";
  return "bg-terakkiBorder";
}

function Chip({ Icon, children, accent }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${
        accent
          ? "bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-white/10 dark:text-terakkiPrimaryLight"
          : "bg-terakkiSurfaceAlt text-terakkiNeutral dark:bg-white/10 dark:text-white/70"
      }`}
    >
      {Icon && <Icon sx={{ fontSize: 14 }} aria-hidden />}
      {children}
    </span>
  );
}

/**
 * Seçili üyenin hedef bazlı ilerleme dökümü.
 * Listedeki bir üyeye tıklanınca sağda açılır; kendi içinde kayar.
 * Yönetici buradan "Hedef Gir" ile kardeş adına son 7 günün grup
 * hedeflerini girebilir (kendi satırı hariç). Grup biliniyorsa üstte
 * İlerleme ve Ünvanlar sekmeleri (mobil MemberDetailView): Ünvanlar üyenin
 * en yüksek ünvanını ve ailelerdeki rozetlerini gösterir.
 */
export default function MemberProgressDetail({ member, onClose, onEditGoals, onRemove, groupId: groupIdProp }) {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const params = useParams();
  // Grup kimliği verilmezse yönetim sayfasının adresinden (yonetim/:groupId).
  const groupId = groupIdProp ?? params.groupId ?? null;
  const [entryOpen, setEntryOpen] = useState(false);
  const [view, setView] = useState("progress");
  // Başka üye seçilince İlerleme'ye dön.
  useEffect(() => setView("progress"), [member.actorId]);
  const showTitles = Boolean(groupId) && view === "titles";
  const canEnter = canEnterFor(groupId, member.actorId, user?._id);
  const percent = memberRate(member);
  // Hedefler ad + biçim anahtarlı; oran sunucunun hedef oranı (gün bazlı).
  const labels = aggregateGoals([member], (activity) => t(ACTIVITY_LABEL_KEYS[activity] || activity));

  return (
    <div
      data-member-detail={member.actorId}
      className="flex flex-col gap-4 lg:h-full lg:min-h-0 rounded-xl border border-terakkiBorder bg-terakkiSurface p-4 dark:bg-white/5 dark:border-white/10"
    >
      <div className="flex items-start gap-3 shrink-0">
        <UserAvatar person={member} size={48} />
        <div className="min-w-0 flex-1">
          <div className="text-lg font-SemiBold text-terakkiInk dark:text-white truncate">
            {member.name} {member.surname}
          </div>
          <div className="text-sm text-terakkiNeutral dark:text-white/60 truncate">{member.email}</div>
        </div>
        <IconButton size="small" onClick={onClose} aria-label={t("close")}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {groupId ? (
        <div role="tablist" className="shrink-0 flex gap-5 border-b border-terakkiBorder dark:border-white/10">
          {[
            ["progress", t("groups_tab_progress")],
            ["titles", t("Ünvanlar")],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={view === key}
              onClick={() => setView(key)}
              className={`-mb-px pb-2 text-sm border-b-2 transition-colors ${
                view === key
                  ? "border-terakkiPrimaryDark text-terakkiPrimaryDark dark:border-white dark:text-white"
                  : "border-transparent text-terakkiNeutral dark:text-white/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {showTitles ? (
        <div className="-mx-4 flex flex-col lg:flex-1 lg:min-h-0">
          <MemberTitlesPanel groupId={groupId} memberId={member.actorId} />
        </div>
      ) : (
        <>
          <div className="shrink-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm text-terakkiNeutral dark:text-white/60">
                {t("manage_stats_table_completion")}
              </span>
              <span className="text-3xl font-light text-terakkiPrimaryDark dark:text-white">%{percent}</span>
            </div>
            <div className="h-1.5 rounded-full bg-terakkiSurfaceAlt dark:bg-white/10 overflow-hidden mt-2">
              <div className={`h-full rounded-full ${toneFor(percent)}`} style={{ width: `${percent}%` }} />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <Chip Icon={EventAvailableIcon}>
              {member.daysActiveCount || 0} {t("manage_stats_table_active_days")}
            </Chip>
            <Chip Icon={StarIcon} accent>
              {member.perfectDaysCount || 0} {t("manage_stats_table_perfect_days")}
            </Chip>
            <Chip>
              {member.totalValue || 0} / {member.totalGoalValue || 0}
            </Chip>
          </div>

          <div className="flex flex-col lg:flex-1 lg:min-h-0">
            <h5 className="text-sm font-SemiBold text-terakkiInk dark:text-white mb-2 shrink-0">
              {t("manage_stats_member_labels")}
            </h5>
            {labels.length === 0 ? (
              <p className="text-sm text-terakkiNeutral dark:text-white/60">{t("manage_stats_no_data")}</p>
            ) : (
              <ul className="flex flex-col gap-3 max-h-72 overflow-y-auto lg:max-h-none lg:flex-1 lg:min-h-0 pr-1">
                {labels.map((item) => (
                  <li key={item.key} data-goal={item.label}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm text-terakkiInk dark:text-white truncate">{item.name}</span>
                      <span className="text-sm font-SemiBold text-terakkiPrimaryDark dark:text-terakkiPrimaryLight shrink-0">
                        {item.value} / {item.goalValue}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-terakkiSurfaceAlt dark:bg-white/10 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${toneFor(item.completion)}`}
                        style={{ width: `${Math.min(100, item.completion)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <div className="flex flex-wrap gap-2 shrink-0">
        {canEnter && (
          <Button variant="outlined" startIcon={<EditNoteOutlinedIcon />} onClick={() => setEntryOpen(true)} fullWidth>
            {t("Hedef Gir")}
          </Button>
        )}
        {onEditGoals && (
          <Button variant="contained" startIcon={<FlagOutlinedIcon />} onClick={() => onEditGoals(member)} fullWidth>
            {t("manage_goals_cta")}
          </Button>
        )}
        {onRemove && (
          <Button
            variant="contained"
            color="error"
            startIcon={<PersonRemoveOutlinedIcon />}
            onClick={() => onRemove(member)}
            fullWidth
          >
            {t("manage_stats_remove_member")}
          </Button>
        )}
      </div>

      {canEnter ? (
        <MemberDayEntryDrawer
          open={entryOpen}
          onClose={() => setEntryOpen(false)}
          groupId={groupId}
          member={{ id: member.actorId, name: member.name, surname: member.surname }}
        />
      ) : null}
    </div>
  );
}
